import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Language as LanguageIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { RootState, AppDispatch } from "../store";
import {
  fetchFirms,
  createFirm,
  updateFirm,
  deleteFirm,
  toggleFirmStatus,
  uploadFirmLogo,
  fetchGlobalStatistics,
} from "../store/slices/firmsSlice";
import {
  Firm,
  FirmCreateInput,
  FirmUpdateInput,
} from "../services/firmsService";
import { COUNTRIES } from "../constants/countries";
import { useToast } from "../hooks/useToast";
import { getApiErrorMessage, getApiFieldErrors } from "../utils/apiError";
import { useConfirm } from "../contexts/ConfirmDialogContext";
import PhoneNumberField from "../components/common/PhoneNumberField";

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
}

const Firms = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const countries = COUNTRIES;
  const { firms, loading, error, statistics, pagination } = useSelector(
    (state: RootState) => state.firms,
  );
  const { showSuccess, showError } = useToast();

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFirm, setSelectedFirm] = useState<Firm | null>(null);
  const [formData, setFormData] = useState<Partial<FirmCreateInput>>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [page, setPage] = useState(0);
  const [firmError, setFirmError] = useState<string | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    dispatch(fetchFirms({ page: page + 1, limit: rowsPerPage }));
    dispatch(fetchGlobalStatistics());
  }, [dispatch, page, rowsPerPage]);

  const handleOpenDialog = (firm?: Firm) => {
    if (firm) {
      setSelectedFirm(firm);
      setFormData({
        name: firm.name,
        email: firm.email,
        phone: firm.phone,
        address: firm.address,
        city: firm.city,
        state: firm.state,
        country: firm.country,
        postal_code: firm.postal_code,
        description: firm.description,
        website: firm.website,
        status: firm.status,
        subscription_plan: firm.subscription_plan,
      });
    } else {
      setSelectedFirm(null);
      setFormData({
        status: "active", // Set default status for new firms
        subscription_plan: "enterprise",
      });
    }
    setLogoFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFirm(null);
    setFormData({
      status: "active", // Reset with default status
      subscription_plan: "enterprise",
    });
    setLogoFile(null);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate required fields
    if (!formData.name?.trim()) {
      errors.name = t("firms.firmNameRequired");
    }

    if (!formData.email?.trim()) {
      errors.email = t("auth.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t("validation.emailInvalid");
    }

    // Validate phone number (international format)
    if (
      formData.phone &&
      !/^\+?[1-9]\d{1,14}$/.test(formData.phone.replace(/\s/g, ""))
    ) {
      errors.phone = t("validation.phoneInvalid");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (field: keyof FirmCreateInput, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (selectedFirm) {
        const updateData: FirmUpdateInput = {
          id: selectedFirm.id,
          status: formData.status,
          ...formData,
        } as FirmUpdateInput;

        await dispatch(
          updateFirm({ firmId: selectedFirm.id, firmData: updateData }),
        ).unwrap();
        showSuccess(t("firms.firmUpdated"));
      } else {
        // Create FormData for firm creation with logo
        const firmFormData = new FormData();

        // Add all form fields
        Object.keys(formData).forEach((key) => {
          const value = formData[key as keyof FirmCreateInput];
          if (value !== undefined && value !== null) {
            firmFormData.append(key, value.toString());
          }
        });

        // Add logo file if selected
        if (logoFile) {
          firmFormData.append("logo", logoFile);
        }

        // Submit with FormData instead of JSON
        await dispatch(createFirm(firmFormData as any)).unwrap();
        showSuccess(t("firms.firmCreated"));
      }

      // For editing, handle logo upload separately if needed
      if (logoFile && selectedFirm) {
        await dispatch(
          uploadFirmLogo({ firmId: selectedFirm.id, file: logoFile }),
        );
      }

      handleCloseDialog();
      dispatch(fetchFirms({ page: page + 1, limit: rowsPerPage }));
    } catch (error: any) {
      // A 422 comes back as `{ errors: { email: [...] } }` — surface it on
      // the actual field as well as in the toast, instead of just a generic
      // "Failed to save firm" message.
      const fieldErrors = getApiFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
      const errorMessage = getApiErrorMessage(error, t("firms.failedToSaveFirm"));
      setFirmError(errorMessage);
      showError(errorMessage);
    }
  };

  const handleDelete = async (firmId: number) => {
    const ok = await confirm(t("firms.confirmDeleteFirm"));
    if (ok) {
      try {
        await dispatch(deleteFirm(firmId)).unwrap();
        showSuccess(t("firms.firmDeleted"));
        dispatch(fetchFirms({ page: page + 1, limit: rowsPerPage }));
      } catch (error: any) {
        showError(getApiErrorMessage(error, t("firms.failedToDeleteFirm")));
      }
    }
  };

  const handleToggleStatus = async (firmId: number, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await dispatch(toggleFirmStatus({ firmId, status: newStatus })).unwrap();
      showSuccess(
        t("firms.statusChanged", {
          status:
            newStatus === "active" ? t("common.active") : t("common.inactive"),
        }),
      );
    } catch (error: any) {
      showError(getApiErrorMessage(error, t("firms.failedToUpdateStatus")));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "warning";
      case "suspended":
        return "error";
      default:
        return "default";
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case "basic":
        return "primary";
      case "professional":
        return "secondary";
      case "enterprise":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">{t("firms.firmsManagement")}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t("firms.addFirm")}
        </Button>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 3 }}>
          <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 23%" } }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {t("dashboard.totalFirms")}
                </Typography>
                <Typography variant="h4">{(statistics as any)?.total_firms || 0}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 23%" } }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {t("common.active")}
                </Typography>
                <Typography variant="h4">{(statistics as any)?.active_firms || 0}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 23%" } }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {t("common.inactive")}
                </Typography>
                <Typography variant="h4">
                  {(statistics as any)?.inactive_firms || 0}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 23%" } }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="error.main">
                  {t("firms.suspended")}
                </Typography>
                <Typography variant="h4">
                  {(statistics as any)?.suspended_firms || 0}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Firms Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("firms.firm")}</TableCell>
                <TableCell>{t("firms.contact")}</TableCell>
                <TableCell>{t("firms.location")}</TableCell>
                <TableCell>{t("firms.plan")}</TableCell>
                <TableCell>{t("common.status")}</TableCell>
                <TableCell>{t("navigation.users")}</TableCell>
                <TableCell>{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {firms.map((firm) => (
                <TableRow key={firm.id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        src={firm.logo_url}
                        sx={{ width: 40, height: 40 }}
                      >
                        <BusinessIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {firm.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {firm.website}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">{firm.email}</Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">{firm.phone}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <LocationIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {firm.city}, {firm.country}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={firm.subscription_plan}
                      color={getPlanColor(firm.subscription_plan) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={firm.status}
                        color={getStatusColor(firm.status) as any}
                        size="small"
                      />
                      <Switch
                        checked={firm.status === "active"}
                        onChange={() =>
                          handleToggleStatus(firm.id, firm.status)
                        }
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {firm.users_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title={t("common.viewDetails")}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/firms/${firm.id}`)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common.edit")}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(firm)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common.delete")}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(firm.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={pagination.total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value));
            setPage(0);
          }}
        />
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedFirm ? t("firms.editFirm") : t("firms.addFirm")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 48%" } }}>
              <TextField
                fullWidth
                label={t("firms.firmName")}
                value={formData.name || ""}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                required
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 48%" } }}>
              <TextField
                fullWidth
                label={t("common.email")}
                type="email"
                value={formData.email || ""}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                required
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 48%" } }}>
              <PhoneNumberField
                value={formData.phone || ""}
                onChange={(value) =>
                  setFormData({ ...formData, phone: value })
                }
                error={formErrors.phone}
                helperText={t("users.enterPhoneWithCountry")}
                required
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 48%" } }}>
              <TextField
                fullWidth
                label={t("firms.website")}
                value={formData.website || ""}
                onChange={(e) =>
                  setFormData({ ...formData, website: e.target.value })
                }
              />
            </Box>
            <Box sx={{ flex: "1 1 100%" }}>
              <TextField
                fullWidth
                label={t("common.address")}
                value={formData.address || ""}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 31%" } }}>
              <TextField
                fullWidth
                label={t("clients.city")}
                value={formData.city || ""}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 31%" } }}>
              <TextField
                fullWidth
                label={t("firms.state")}
                value={formData.state || ""}
                onChange={(e) =>
                  setFormData({ ...formData, state: e.target.value })
                }
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 31%" } }}>
              <TextField
                fullWidth
                label={t("firms.postalCode")}
                value={formData.postal_code || ""}
                onChange={(e) =>
                  setFormData({ ...formData, postal_code: e.target.value })
                }
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 31%" } }}>
              <TextField
                fullWidth
                select
                label={t("clients.country")}
                value={formData.country || ""}
                onChange={(e) =>
                  setFormData({ ...formData, country: e.target.value })
                }
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 31%" } }}>
              <TextField
                fullWidth
                select
                label={t("firms.subscriptionPlan")}
                value={formData.subscription_plan || "enterprise"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    subscription_plan: e.target.value as any,
                  })
                }
              >
                <MenuItem value="basic">{t("firms.basic")}</MenuItem>
                <MenuItem value="professional">
                  {t("firms.professional")}
                </MenuItem>
                <MenuItem value="enterprise">{t("firms.enterprise")}</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 31%" } }}>
              <TextField
                fullWidth
                select
                label={t("common.status")}
                value={formData.status || "active"}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value as any })
                }
              >
                <MenuItem value="active">{t("common.active")}</MenuItem>
                <MenuItem value="inactive">{t("common.inactive")}</MenuItem>
                <MenuItem value="suspended">{t("firms.suspended")}</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ flex: "1 1 100%" }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("common.description")}
                value={formData.description || ""}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Box>
            <Box sx={{ flex: "1 1 100%" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                {t("firms.firmLogo")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  {t("firms.uploadLogo")}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  />
                </Button>
                {logoFile && (
                  <Typography variant="caption" sx={{ ml: 2 }}>
                    {t("firms.selectedFile", { fileName: logoFile.name })}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("common.cancel")}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedFirm ? t("common.update") : t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default Firms;
